import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getCustodialHolding, getCustodialHoldingsForUserId } from "../../core/db/custodial";
import { 
    CustodialHolding,
    CustodialHoldingError,
    CustodialHoldingState,
    FetchHoldingByCustodialHoldingIdParams,
    FetchHoldingsByUserIdParams,
} from "./types";

const initialState: CustodialHoldingState = {
    ownedTickets: new Map<number, CustodialHolding>(),
    loading: false,
    error: CustodialHoldingError.NONE,
}

export const fetchHoldingsForUser = createAsyncThunk('custodialHoldings/fetchHoldingsForUser',
    async (args: FetchHoldingsByUserIdParams) => {
    const custodialHoldings = await getCustodialHoldingsForUserId(args.supabase, args.userId);
    return custodialHoldings
})

export const fetchHoldingForUser = createAsyncThunk('custodialHoldings/fetchHoldingForUser',
    async (args: FetchHoldingByCustodialHoldingIdParams) => {
    const custodialHolding = await getCustodialHolding(args.supabase, args.custodialHoldingId);
    return custodialHolding
})

export const custodialHoldingsSlice = createSlice({
    name: 'custodialHoldings',
    initialState,
    reducers: {
        loading: (state) => { state.loading = true },
        fetched: (state) => { state.loading = false },
        clear: (state) => { state = initialState },
    },
    extraReducers: (builder) => {
        // === Fetch Holdings ===
        builder.addCase(fetchHoldingsForUser.pending, (state) => {
            console.log('PENDING');
            state.loading = true
        })
        builder.addCase(fetchHoldingsForUser.fulfilled, (state, action: PayloadAction<CustodialHolding[]>) => {
            console.log('FULFILLED');
            // Add holdings to state
            const newHoldingsMap = new Map<number, CustodialHolding>();

            action.payload.map((holding) => newHoldingsMap.set(holding.id, holding));

            state.ownedTickets = newHoldingsMap;
            state.loading = false;
            state.error = CustodialHoldingError.NONE;
        })
        builder.addCase(fetchHoldingsForUser.rejected, (state) => {
            console.log('REJECTED');
            state.loading = false;
            state.error = CustodialHoldingError.LOADING_FAILED;
        })

        // === Fetch Holding ===
        builder.addCase(fetchHoldingForUser.pending, (state) => {
            console.log('PENDING');
            state.loading = true
        })
        builder.addCase(fetchHoldingForUser.fulfilled, (state, action: PayloadAction<CustodialHolding>) => {
            console.log('FULFILLED');
            // Add holdings to state
            const newHoldingsMap = new Map<number, CustodialHolding>(state.ownedTickets);
            newHoldingsMap.set(action.payload.id, action.payload);

            state.ownedTickets = newHoldingsMap;
            state.loading = false;
            state.error = CustodialHoldingError.NONE;
        })
        builder.addCase(fetchHoldingForUser.rejected, (state) => {
            console.log('REJECTED');
            state.loading = false;
            state.error = CustodialHoldingError.LOADING_FAILED;
        })
    }
})

// Selectors
export const selectHoldings = (state: CustodialHoldingState) => state.ownedTickets;
export const selectHolding = (state: CustodialHoldingState, ticketId: number) => state.ownedTickets.get(ticketId);
export const selectHoldingTicketAddress = (state: CustodialHoldingState, ticketId: number) => state.ownedTickets.get(ticketId)?.ticketAddress;
export const selectHoldingError = (state: CustodialHoldingState) => state.error;
export const selectHoldingFetchStatus = (state: CustodialHoldingState) => state.loading;

export default custodialHoldingsSlice.reducer